import { useCallback, useState } from "react";
import { useApi } from "./useApi";
import type {
    VariantGroupResponse,
    CreateVariantGroupRequest,
    UpdateVariantGroupRequest,
    CreateVariantGroupResponse,
} from "@/models/variantGroup";

const VARIANT_GROUPS_ENDPOINT = "/variantGroups";

export const useVariantGroupApi = () => {
    const { callApi } = useApi();
    const [isFetching, setIsFetching] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    const createVariantGroup = useCallback(
        async (
            data: CreateVariantGroupRequest,
        ): Promise<CreateVariantGroupResponse> => {
            setIsProcessing(true);
            
            try {
                return callApi<CreateVariantGroupResponse>(
                    VARIANT_GROUPS_ENDPOINT,
                    {
                        method: "POST",
                        body: JSON.stringify(data),
                    },
                );
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi],
    );

    const getAllVariantGroups = useCallback(async (): Promise<
        VariantGroupResponse[]
    > => {
        setIsFetching(true);

        try {
            return callApi<VariantGroupResponse[]>(VARIANT_GROUPS_ENDPOINT);
        } finally {
            setIsFetching(false);
        }
    }, [callApi]);

    const getVariantGroup = useCallback(
        async (id: number): Promise<VariantGroupResponse> => {
            setIsFetching(true);

            try {
                return callApi<VariantGroupResponse>(
                    `${VARIANT_GROUPS_ENDPOINT}/${id}`,
                );
            } finally {
                setIsFetching(false);
            }
        },
        [callApi],
    );

    const updateVariantGroup = useCallback(
        async (id: number, data: UpdateVariantGroupRequest): Promise<void> => {
            setIsProcessing(true);

            try {
                return callApi(`${VARIANT_GROUPS_ENDPOINT}/${id}`, {
                    method: "PUT",
                    body: JSON.stringify(data),
                });
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi],
    );

    const deleteVariantGroup = useCallback(
        async (id: number): Promise<void> => {
            setIsProcessing(true);

            try {
                await callApi(`${VARIANT_GROUPS_ENDPOINT}/${id}`, {
                    method: "DELETE",
                });
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi],
    );

    return {
        createVariantGroup,
        getAllVariantGroups,
        getVariantGroup,
        updateVariantGroup,
        deleteVariantGroup,
        isFetching,
        isProcessing,
    };
};
